
import { useState, useEffect } from "react"
import "./components/Card";
import { Button } from "./components/Button"
import { Badge } from "./components/Badge"
import { Textarea } from "./components/TextArea"
import { Label } from "./components/Label"
import { Input } from "./components/Input"
import services from "../../API";
import { useSelector } from "react-redux"
import CreateAudienceModal from "../Ads/CreateAudienceModal"
import {
    Plus,
    Clock,
    Minus,
} from "lucide-react"
import { AspectRatioIcon, getIconComponent, contentTypes } from "../Ads/GenerateContentModal"
import axios from "axios"
import TimePicker from "./components/TimePicker"
import { convertTo12Hour } from "./CreateCampaign"

const scheduleOptions = [
    { id: "Daily", label: "Daily", description: "Generate content every day" },
    { id: "Every_Other_Day", label: "Every Other Day", description: "Generate content every 2 days" },
    { id: "Twice_Weekly", label: "Twice Weekly", description: "Generate content twice per week" },
    { id: "Weekly", label: "Weekly", description: "Generate content once per week" },
    { id: "Custom_Schedule", label: "Custom Schedule", description: "Set your own schedule" },
]

const aspectRatios = [
    { id: "1:1", value: "1:1", label: "Square (1:1)" },
    { id: "9:16", value: "9:16", label: "Vertical (9:16)" },
    { id: "16:9", value: "16:9", label: "Wide (16:9)" },
    { id: "4:5", value: "4:5", label: "Portrait (4:5)" },
];

const EditCampaignModal = ({
    open,
    onOpenChange,
    campaign,
    onCampaignUpdated,
}) => {
    const brands = useSelector((state) => state.data.apiData.brands?.data || []);
    const [isUpdating, setIsUpdating] = useState(false)
    const [editingAudience, setEditingAudience] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Initialize form state with campaign data
    const [campaignName, setCampaignName] = useState("")
    const [campaignDescription, setCampaignDescription] = useState("")
    const [selectedContentType, setSelectedContentType] = useState("")
    const [selectedAudience, setSelectedAudience] = useState("")
    const [selectedBrand, setSelectedBrand] = useState("")
    const [prompt, setPrompt] = useState("")
    const [contentCount, setContentCount] = useState(1)
    useState(null);

    const [scheduleType, setScheduleType] = useState("")
    const [scheduleTime, setScheduleTime] = useState("09:00")
    const [customDays, setCustomDays] = useState([])
    const [deliveryEmail, setDeliveryEmail] = useState("")

    useState([]);
    const [brandSearchTerm, setBrandSearchTerm] = useState("");
    const [showBrandDropdown, setShowBrandDropdown] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedSize, setSelectedSize] = useState("1:1");
    const [audiences, setAudiences] = useState([]);

    const handleAspectRatioChange = (ratio) => {
        setSelectedSize(ratio);
        setIsDropdownOpen(false);
    };

    // Filter brands based on search term
    const filteredBrands = brands.filter(
        (brand) =>
            brand.internalName
                ?.toLowerCase()
                .includes(brandSearchTerm?.toLowerCase()) ||
            brand?.description
                ?.toLowerCase()
                ?.includes(brandSearchTerm?.toLowerCase()) ||
            brand?.industry?.toLowerCase()?.includes(brandSearchTerm?.toLowerCase())
    );

    const handleBrandSelect = async (brandId) => {
        setSelectedBrand(brandId);
        setShowBrandDropdown(false);
    }

    const fetchBrands = () => {
        try {
            services.getBrands()
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        fetchBrands();
    }, [])

    // Initialize form with campaign data when modal opens or campaign changes
    useEffect(() => {
        if (campaign && open) {
            setCampaignName(campaign?.name || "")
            setCampaignDescription(campaign?.description || "")
            setSelectedContentType(campaign?.contentType || "ads")
            setSelectedAudience(campaign?.audienceId?._id || "")
            setSelectedBrand(campaign?.brandId?._id || campaign?.brandId || "")
            setPrompt(campaign?.contentPrompt || "")
            setContentCount(campaign?.contentQuantity || 1)
            setSelectedSize(campaign?.contentSize || "1:1")
            setScheduleType(campaign?.scheduleAt || "")
            setScheduleTime(campaign?.deliveryTime || "09:00")
            setCustomDays(campaign?.selectedDays || [])
            setDeliveryEmail(campaign?.email || JSON.parse(localStorage?.getItem("userData"))?.email || "")
        }
    }, [campaign, open])

    useEffect(() => {
        if (!open) {
            // Reset form when modal closes
            setCampaignName("")
            setCampaignDescription("")
            setSelectedContentType("ads")
            setSelectedAudience("")
            setSelectedBrand("")
            setPrompt("")
            setContentCount(1)
            setSelectedSize("1:1")
            setScheduleType("")
            setScheduleTime("09:00")
            setCustomDays([])
            setIsUpdating(false)
        }
    }, [open])

    const handleUpdateCampaign = async () => {
        setIsUpdating(true)
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const updatedCampaign = Object.fromEntries(Object.entries({
            _id: campaign?._id,
            name: campaignName,
            description: campaignDescription,
            email: deliveryEmail,
            brandId: selectedBrand,
            audienceId: selectedAudience,
            contentType: selectedContentType,
            deliveryTime: scheduleTime,
            timezone,
            scheduleAt: scheduleType,
            selectedDays: customDays,
            contentSize: selectedSize,
            contentQuantity: contentCount,
            contentPrompt: prompt,
            isActive: campaign?.isActive !== undefined ? campaign.isActive : true,
        }).filter(([, value]) => value !== ""));

        try {
            await services.updateCampaign(updatedCampaign?._id, updatedCampaign)
            onCampaignUpdated(prev => !prev);
            onOpenChange(false)
        } catch (error) {
            console.error("Error updating campaign:", error);
            setIsUpdating(false)
        }
    }

    // Fetch audiences from API
    useEffect(() => {
        const token = localStorage.getItem("token");
        const fetchAudiences = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API}audiences`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
                setAudiences(response.data.data);
            } catch (err) {
                setAudiences([]);
            }
        };
        fetchAudiences();
    }, [showCreateModal]);

    // const allAudiences = [...audiences, ...customAudiences]

    const handleSaveAudience = (audienceData) => {
        if (editingAudience) {
            // Update existing audience
            setAudiences((prev) =>
                prev.map((audience) =>
                    audience.id === editingAudience.id
                        ? { ...audienceData, id: editingAudience.id }
                        : audience
                )
            );
            setEditingAudience(null);
        } else {
            // Create new audience
            setAudiences((prev) => [
                ...prev,
                {
                    ...audienceData,
                    createdAt: new Date().toISOString().split("T")[0],
                    usageCount: 0,
                },
            ]);
        }
        setShowCreateModal(false);
    };

    const canUpdateCampaign = campaignName.trim() !== "" &&
        deliveryEmail.trim() !== "" &&
        selectedContentType !== "" &&
        scheduleType !== ""

    // Get brand name for display
    const getSelectedBrandName = () => {
        if (!selectedBrand) return "Select Brand"
        const brand = brands.find(b => b._id === selectedBrand)
        return brand ? brand.internalName : "Select Brand"
    }

    return (
        <>
            {open && (
                <div
                    className="automation-modal-overlay fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-[99]"
                    onClick={() => onOpenChange(false)}
                >
                    <div
                        className="automation-modal bg-white dark:bg-[#0A0A0A] rounded-lg max-w-[1000px] w-full mx-4 max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-[#0A0A0A] z-10">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-[#fff]">
                                        Edit Campaign
                                    </h2>
                                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                                        Update your automated content generation settings
                                    </p>
                                </div>

                                <button
                                    onClick={() => {
                                        onOpenChange(false)
                                    }}
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                                >
                                    <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Body - Scrollable Content */}
                        <div className="automation-modal-body p-6 space-y-8">
                            {/* Campaign Details Section */}
                            <div className="space-y-6">
                                <div className="text-center">
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-[#fff] mb-2">Campaign Details</h3>
                                    <p className="text-gray-600 dark:text-gray-400">Update your campaign name and email delivery settings</p>
                                </div>

                                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                                    <div className="automation-brand-row flex items-center gap-2">
                                        <div className="text-gray-700 dark:text-gray-300 flex items-center justify-center gap-1 w-full ">
                                            Campaign will be generated for:{" "}
                                            <div className="automation-brand-select relative">
                                                <button
                                                    className="w-64 px-4 py-2 rounded-lg text-gray-600 dark:text-gray-300 bg-orange-100 dark:bg-orange-900/30 hover:bg-orange-200 dark:hover:bg-orange-900/50 flex items-center justify-between"
                                                    onClick={() => setShowBrandDropdown(!showBrandDropdown)}
                                                >
                                                    <span className="truncate w-64 overflow-hidden whitespace-nowrap">
                                                        <strong>
                                                            {getSelectedBrandName()}
                                                        </strong>
                                                    </span>
                                                    <svg
                                                        className="h-5 w-5"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M19 9l-7 7-7-7"
                                                        />
                                                    </svg>
                                                </button>

                                                {showBrandDropdown && (
                                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-[#0A0A0A] border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-80 overflow-hidden">
                                                        <div className="p-3 border-b border-gray-300 dark:border-gray-600">
                                                            <div className="relative">
                                                                <svg
                                                                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    viewBox="0 0 24 24"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth={2}
                                                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                                                    />
                                                                </svg>
                                                                <input
                                                                    placeholder="Search brands..."
                                                                    value={brandSearchTerm}
                                                                    onChange={(e) => setBrandSearchTerm(e.target.value)}
                                                                    className="pl-10 h-9 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-[#fff] placeholder-gray-500 dark:placeholder-gray-400 focus:border-gray-400 dark:focus:border-gray-500 focus:ring-0 w-full"
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="max-h-60 overflow-y-auto">
                                                            {filteredBrands.length > 0 ? (
                                                                filteredBrands.map((brand, index) => (
                                                                    <button
                                                                        key={index}
                                                                        onClick={() => handleBrandSelect(brand._id)}
                                                                        className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2 text-gray-900 dark:text-[#fff]"
                                                                    >
                                                                        {selectedBrand === brand._id ? (
                                                                            <span className="text-green-500">✓</span>
                                                                        ) : (
                                                                            <span className="w-4"></span>
                                                                        )}
                                                                        <span>{brand.internalName}</span>
                                                                    </button>
                                                                ))
                                                            ) : (
                                                                <div className="px-4 py-6 text-center text-gray-400 dark:text-gray-500">
                                                                    <div className="text-sm">No brands found</div>
                                                                    <div className="text-xs">
                                                                        Try adjusting your search
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="max-w-2xl mx-auto space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="campaign-name" className="text-gray-700 dark:text-gray-300">Campaign Name *</Label>
                                        <Input
                                            id="campaign-name"
                                            placeholder="e.g., Daily TechFlow Ads"
                                            value={campaignName}
                                            className="w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff] border-gray-300 dark:border-gray-600"
                                            onChange={(e) => setCampaignName(e.target.value)}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="campaign-description" className="text-gray-700 dark:text-gray-300">Description (Optional)</Label>
                                        <Textarea
                                            id="campaign-description"
                                            placeholder="Brief description of this campaign's purpose"
                                            value={campaignDescription}
                                            onChange={(e) => setCampaignDescription(e.target.value)}
                                            className="min-h-[80px] px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff] border-gray-300 dark:border-gray-600"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="delivery-email" className="text-gray-700 dark:text-gray-300">Email Address for Content Delivery *</Label>
                                        <Input
                                            id="delivery-email"
                                            type="email"
                                            disabled
                                            className="w-full bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-[#fff] border-gray-300 dark:border-gray-600"
                                            placeholder="your@email.com"
                                            value={deliveryEmail}
                                            onChange={(e) => setDeliveryEmail(e.target.value)}
                                        />
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            Generated content will be sent to this email address according to your schedule
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Content Type Selection Section */}
                            <div className="space-y-4">
                                <div className="text-center">
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-[#fff] mb-2">What content do you want to generate?</h3>
                                    <p className="text-gray-600 dark:text-gray-400">Choose the type of content for your scheduled campaign</p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {contentTypes.map((type) => {
                                        const isAds = type.id === "ads";
                                        const selectedContentType = "ads"

                                        return (
                                            <div
                                                key={type.id}
                                                className={`p-4 transition-all border-2 rounded-lg ${isAds
                                                    ? `cursor-pointer ${selectedContentType === type.id
                                                        ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
                                                        : type.color
                                                    }`
                                                    : "opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                                                    }`}
                                                tabIndex={isAds ? 0 : -1}
                                                aria-disabled={!isAds}
                                            >
                                                <div className="text-center space-y-3">
                                                    <div
                                                        className={`w-10 h-10 mx-auto rounded-lg flex items-center justify-center ${isAds
                                                            ? selectedContentType === type.id
                                                                ? "bg-orange-500 text-white"
                                                                : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                                                            : "bg-gray-200 dark:bg-gray-600 text-gray-400 dark:text-gray-500"
                                                            }`}
                                                    >
                                                        {getIconComponent(type.icon)}
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900 dark:text-[#fff] text-sm">
                                                            {type.title}
                                                        </h4>
                                                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                                            {type.description}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Audience Selection Section - Updated to match Create Modal */}
                            <div className="space-y-4">
                                <div className="text-center">
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-[#fff] mb-2">
                                        Who is your target audience? (Optional)
                                    </h3>
                                    <p className="text-gray-600 dark:text-gray-400">
                                        Selecting an audience helps create more targeted content, or
                                        skip to use your brand's default audience
                                    </p>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {/* Add Custom Audience Card - First */}
                                    <div
                                        className="p-4 cursor-pointer transition-all border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-orange-300 dark:hover:border-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg"
                                        onClick={() => {
                                            setEditingAudience(null);
                                            setShowCreateModal(true);
                                        }}
                                    >
                                        <div className="space-y-3 text-center">
                                            <div className="w-8 h-8 mx-auto rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                                {getIconComponent("Plus")}
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900 dark:text-[#fff] text-sm">
                                                    Create New Audience
                                                </h4>
                                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                                    Define your own target audience
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    {/* API Audiences */}
                                    {audiences.length === 0 && (
                                        <div className="col-span-full text-center text-gray-400 dark:text-gray-500 py-8">
                                            No audiences found.
                                        </div>
                                    )}
                                    {audiences.map((audience) => (
                                        <div
                                            key={audience._id}
                                            className={`p-4 cursor-pointer transition-all border-2 rounded-lg ${selectedAudience === audience._id
                                                ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
                                                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
                                                }`}
                                            onClick={() => setSelectedAudience(audience._id)}
                                        >
                                            <div className="space-y-3">
                                                <div className="flex items-start space-x-3">
                                                    <div
                                                        className={`w-8 h-8 rounded-lg flex items-center justify-center ${selectedAudience === audience._id
                                                            ? "bg-orange-500 text-white"
                                                            : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                                                            }`}
                                                    >
                                                        {getIconComponent(audience.icon || "Users")}
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <h4 className="font-semibold text-gray-900 dark:text-[#fff] text-sm">
                                                                {audience.name || audience.title}
                                                            </h4>
                                                            {audience.isCustom && (
                                                                <span className="border border-gray-300 dark:border-gray-600 px-1.5 py-0.5 rounded text-xs text-gray-700 dark:text-gray-300">
                                                                    Custom
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                                            {audience.description}
                                                        </p>
                                                    </div>
                                                </div>
                                                {audience.demographics && (
                                                    <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                                                        <span className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded text-xs">
                                                            {audience.demographics}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Schedule Configuration Section */}
                            <div className="space-y-6">
                                <div className="text-center">
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-[#fff] mb-2">Set Your Schedule</h3>
                                    <p className="text-gray-600 dark:text-gray-400">Choose how often you want to receive generated content</p>
                                </div>

                                <div className="max-w-3xl mx-auto space-y-6">
                                    {/* Schedule Type Selection */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {scheduleOptions.map((option) => (
                                            <div
                                                key={option.id}
                                                className={`p-4 cursor-pointer rounded-2xl transition-all border-2 ${scheduleType === option.id
                                                    ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
                                                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
                                                    }`}
                                                onClick={() => setScheduleType(option.id)}
                                            >
                                                <div className="flex items-start space-x-3">
                                                    <div
                                                        className={`w-8 h-8 rounded-lg flex items-center justify-center ${scheduleType === option.id
                                                            ? "bg-orange-500 text-white"
                                                            : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                                                            }`}
                                                    >
                                                        <Clock className="w-4 h-4" />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900 dark:text-[#fff] text-sm">{option.label}</h4>
                                                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{option.description}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {/* Schedule Time */}
                                    <div className="space-y-2">
                                        <Label htmlFor="schedule-time" className="text-gray-700 dark:text-gray-300">Delivery Time</Label>
                                        <div className="automation-schedule-time flex items-center space-x-2">
                                            <TimePicker
                                                value={scheduleTime}
                                                onChange={setScheduleTime}
                                                className="w-32"
                                            />
                                            <span className="text-sm text-gray-500 dark:text-gray-400">Content will be generated and sent at this time</span>
                                        </div>
                                    </div>

                                    {/* Custom Days Selection (for twice weekly and custom) */}
                                    {(scheduleType === "Twice_Weekly" || scheduleType === "Custom_Schedule") && (
                                        <div className="space-y-2">
                                            <Label className="text-gray-700 dark:text-gray-300">Select Days</Label>
                                            <div className="automation-days grid grid-cols-4 gap-2 sm:grid-cols-7">
                                                {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => (
                                                    <Button
                                                        key={day}
                                                        type="button"
                                                        variant={customDays.includes(day) ? "default" : "outline"}
                                                        size="sm"
                                                        aria-label={day}
                                                        onClick={() => {
                                                            if (customDays.includes(day)) {
                                                                setCustomDays(customDays.filter((d) => d !== day))
                                                            } else {
                                                                if (scheduleType === "Twice_Weekly" && customDays.length >= 2) {
                                                                    return;
                                                                }
                                                                setCustomDays([...customDays, day])
                                                            }
                                                        }}
                                                        className={customDays.includes(day)
                                                            ? "bg-orange-500 hover:bg-orange-600 text-white"
                                                            : "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600"}
                                                    >
                                                        <span className="sm:hidden">{day.slice(0, 3)}</span><span className="hidden sm:inline">{day}</span>
                                                    </Button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Content Parameters Section */}
                            <div className="space-y-6">
                                <div className="text-center">
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-[#fff] mb-2">Content Parameters</h3>
                                    <p className="text-gray-600 dark:text-gray-400">Configure the content generation settings</p>
                                </div>

                                <div className="max-w-2xl mx-auto space-y-6">
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="content-count" className="text-gray-700 dark:text-gray-300">Content Quantity</Label>
                                                <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 w-32">
                                                    <button
                                                        type="button"
                                                        onClick={() => setContentCount(Math.max(1, contentCount - 1))}
                                                        className="p-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-orange-600 rounded-l-lg"
                                                    >
                                                        <Minus className="w-4 h-4" />
                                                    </button>
                                                    <input
                                                        type="number"
                                                        value={contentCount}
                                                        onChange={(e) => setContentCount(Math.max(1, Number.parseInt(e.target.value) || 1))}
                                                        className="flex-1 text-center text-base font-medium border-0 focus:outline-none py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                        min="1"
                                                        max="10"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setContentCount(Math.min(10, contentCount + 1))}
                                                        className="p-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-orange-600 rounded-r-lg"
                                                    >
                                                        <Plus className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    What size do you want?
                                                </label>
                                                <div className="relative">
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                                        className="w-full px-3 py-3 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-gray-800 text-left flex items-center gap-3 text-gray-900 dark:text-[#fff]"
                                                    >
                                                        <AspectRatioIcon ratio={selectedSize} />
                                                        <span>
                                                            {
                                                                aspectRatios.find((r) => r.id === selectedSize)
                                                                    ?.label
                                                            }
                                                        </span>
                                                        <svg
                                                            className="w-4 h-4 text-gray-400 dark:text-gray-500 ml-auto"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M19 9l-7 7-7-7"
                                                            />
                                                        </svg>
                                                    </button>

                                                    {isDropdownOpen && (
                                                        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-[#0A0A0A] border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-10">
                                                            <div className="p-2">
                                                                {aspectRatios.map((ratio) => (
                                                                    <button
                                                                        key={ratio.value}
                                                                        type="button"
                                                                        onClick={() => handleAspectRatioChange(ratio.id)}
                                                                        className="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 rounded flex items-center gap-3 text-sm text-gray-900 dark:text-[#fff]"
                                                                    >
                                                                        <AspectRatioIcon ratio={ratio.id} />
                                                                        <span>{ratio.label}</span>
                                                                        {selectedSize === ratio.id && (
                                                                            <svg
                                                                                className="w-4 h-4 text-green-500 ml-auto"
                                                                                fill="none"
                                                                                stroke="currentColor"
                                                                                viewBox="0 0 24 24"
                                                                            >
                                                                                <path
                                                                                    strokeLinecap="round"
                                                                                    strokeLinejoin="round"
                                                                                    strokeWidth={2}
                                                                                    d="M5 13l4 4L19 7"
                                                                                />
                                                                            </svg>
                                                                        )}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="campaign-prompt" className="text-gray-700 dark:text-gray-300">Content Prompt (Optional)</Label>
                                            <Textarea
                                                id="campaign-prompt"
                                                placeholder="Add specific instructions for content generation (optional)"
                                                value={prompt}
                                                onChange={(e) => setPrompt(e.target.value)}
                                                className="min-h-[80px] px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff] border-gray-300 dark:border-gray-600"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Campaign Summary Section */}
                            <div className="space-y-6">
                                <div className="text-center">
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-[#fff] mb-2">Campaign Summary</h3>
                                    <p className="text-gray-600 dark:text-gray-400">Review your campaign settings before updating</p>
                                </div>

                                <div className="max-w-2xl mx-auto">
                                    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                                        <h4 className="font-medium text-gray-900 dark:text-[#fff] mb-3">Campaign Summary</h4>
                                        <div className="automation-summary space-y-2 text-sm">
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600 dark:text-gray-400">Campaign Name:</span>
                                                <Badge variant="outline" className="text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600">{campaignName || "Untitled Campaign"}</Badge>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600 dark:text-gray-400">Content Type:</span>
                                                <Badge variant="outline" className="text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600">Ads</Badge>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600 dark:text-gray-400">Audience:</span>
                                                <Badge variant="outline" className="text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600">{audiences.find((a) => a._id === selectedAudience)?.name || "Not selected"}</Badge>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600 dark:text-gray-400">Schedule:</span>
                                                <Badge variant="outline" className="text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600">{scheduleOptions.find((s) => s.id === scheduleType)?.label}</Badge>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600 dark:text-gray-400">Delivery Time:</span>
                                                <Badge variant="outline" className="text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600">{convertTo12Hour(scheduleTime)}</Badge>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600 dark:text-gray-400">Email:</span>
                                                <Badge variant="outline" className="text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600">{deliveryEmail}</Badge>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600 dark:text-gray-400">Content per delivery:</span>
                                                <Badge variant="outline" className="text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600">
                                                    {contentCount} {aspectRatios.find((s) => s.id === selectedSize)?.label}
                                                </Badge>
                                            </div>
                                            {prompt && (
                                                <div className="flex items-start justify-between">
                                                    <span className="text-gray-600 dark:text-gray-400">Custom Prompt:</span>
                                                    <div className="text-right max-w-xs">
                                                        <Badge variant="outline" className="text-xs text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600">
                                                            {prompt.substring(0, 50)}
                                                            {prompt.length > 50 ? "..." : ""}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="automation-modal-actions flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700 relative bottom-0 bg-white dark:bg-[#0A0A0A]">
                                <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isUpdating} className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">
                                    Cancel
                                </Button>
                                <div className="flex gap-3">
                                    <Button
                                        onClick={handleUpdateCampaign}
                                        disabled={isUpdating || !canUpdateCampaign}
                                        className="text-white"
                                        style={{ backgroundColor: "#ff6600" }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = "#e55a00"
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = "#ff6600"
                                        }}
                                    >
                                        {isUpdating ? "Updating Campaign..." : "Update Campaign"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <CreateAudienceModal
                open={showCreateModal}
                onOpenChange={setShowCreateModal}
                onSave={handleSaveAudience}
                editingAudience={editingAudience}
            />
        </>
    )
}

export default EditCampaignModal
